import { MutableRefObject, useCallback, useEffect, useMemo, useRef } from "react";
import { reportPlayerState, stopActiveEncoding } from "../api/jellyfin";
import { Credentials } from "../storage/credentials";
import {
  createPlaybackSessionReporter,
  PlaybackReportSnapshot,
} from "./sessionReporter";
import { usePlayerPageExit } from "./usePlayerPageExit";

type PlayMethod = PlaybackReportSnapshot["playMethod"];

type PlayerSessionContext = {
  credentials: Credentials | null;
  itemId: string;
  mediaSourceId?: string;
  playSessionId?: string;
  playMethod: PlayMethod;
  positionRef: MutableRefObject<number>;
  pausedRef: MutableRefObject<boolean>;
  mutedRef: MutableRefObject<boolean>;
  startedRef: MutableRefObject<boolean>;
};

/** Owns exactly one server-side playback generation at a time. */
export function usePlayerSession(context: PlayerSessionContext) {
  const reporter = useMemo(
    () => context.credentials
      ? createPlaybackSessionReporter((event, snapshot) =>
          reportPlayerState(context.credentials!, event, snapshot))
      : null,
    [context.credentials],
  );
  const contextRef = useRef(context);
  const startedRef = context.startedRef;
  const generationRef = useRef<Pick<
    PlaybackReportSnapshot,
    "itemId" | "mediaSourceId" | "playSessionId" | "playMethod"
  > | null>(null);
  const activeEncodingRef = useRef<{
    credentials: Credentials;
    playSessionId: string;
  } | null>(null);
  contextRef.current = context;

  const snapshot = useCallback((): PlaybackReportSnapshot => {
    const current = contextRef.current;
    const generation = generationRef.current;
    return {
      itemId: generation?.itemId ?? current.itemId,
      mediaSourceId: generation?.mediaSourceId ?? current.mediaSourceId,
      playSessionId: generation?.playSessionId ?? current.playSessionId,
      positionSeconds: current.positionRef.current,
      paused: current.pausedRef.current,
      muted: current.mutedRef.current,
      playMethod: generation?.playMethod ?? current.playMethod,
    };
  }, []);

  const start = useCallback(() => {
    if (!reporter) return;
    const current = contextRef.current;
    generationRef.current = {
      itemId: current.itemId,
      mediaSourceId: current.mediaSourceId,
      playSessionId: current.playSessionId,
      playMethod: current.playMethod,
    };
    startedRef.current = true;
    reporter.start(snapshot());
  }, [reporter, snapshot]);

  const progress = useCallback(() => {
    if (!reporter || !startedRef.current) return;
    reporter.progress(snapshot());
  }, [reporter, snapshot]);

  const stop = useCallback(() => {
    if (!reporter || !startedRef.current) return;
    reporter.stop(snapshot());
    startedRef.current = false;
    generationRef.current = null;
  }, [reporter, snapshot]);

  useEffect(() => () => stop(), [stop, context.itemId]);
  const serverUrl = context.credentials?.serverUrl;
  const accessToken = context.credentials?.accessToken;
  const deviceId = context.credentials?.deviceId;
  const userId = context.credentials?.userId;
  useEffect(() => {
    const next = serverUrl && accessToken && deviceId && userId && context.playSessionId
      ? { serverUrl, accessToken, deviceId, userId }
      : null;
    const previous = activeEncodingRef.current;
    if (previous && (
      previous.playSessionId !== context.playSessionId ||
      previous.credentials.serverUrl !== serverUrl ||
      previous.credentials.accessToken !== accessToken ||
      previous.credentials.deviceId !== deviceId ||
      previous.credentials.userId !== userId
    )) {
      void stopActiveEncoding(previous.credentials, previous.playSessionId)
        .catch(() => undefined);
    }
    activeEncodingRef.current = next && context.playSessionId
      ? { credentials: next, playSessionId: context.playSessionId }
      : null;
  }, [accessToken, deviceId, context.playSessionId, serverUrl, userId]);
  useEffect(() => () => {
    const active = activeEncodingRef.current;
    activeEncodingRef.current = null;
    if (active) {
      void stopActiveEncoding(active.credentials, active.playSessionId)
        .catch(() => undefined);
    }
  }, []);
  usePlayerPageExit(() => {
    const active = activeEncodingRef.current;
    if (active) {
      void stopActiveEncoding(active.credentials, active.playSessionId)
        .catch(() => undefined);
    }
  });

  return { startedRef, start, progress, stop };
}
